import includes from 'lodash/includes';
import isEqual from 'lodash/isEqual';
import merge from 'lodash/merge';
import qs from 'qs';
import { useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import { getCleanObject } from '@hero/utils';

export function useQueries() {
  const location = useLocation();
  const navigate = useNavigate();

  const queries = useMemo(() => {
    return qs.parse(location.search, { ignoreQueryPrefix: true, comma: true }) || {};
  }, [location.search]);

  const addQuery = (key, value, replace = false) => {
    if (!key || queries[key] === value) return;
    navigate(
      {
        search: qs.stringify(
          {
            ...queries,
            [key]: value
          },
          { arrayFormat: 'comma', encode: false }
        )
      },
      {
        replace
      }
    );
  };

  const addQueries = newQueries => {
    const cleanNewQueries = getCleanObject(newQueries, { isEmptyArray: false });

    if (!includes(queries, cleanNewQueries)) {
      navigate({
        search: qs.stringify(merge({}, queries, cleanNewQueries), {
          arrayFormat: 'comma',
          encode: false
        })
      });
    }
  };

  const addNewQueries = newQueries => {
    const cleanNewQueries = getCleanObject(newQueries);
    if (!isEqual(cleanNewQueries, queries)) {
      navigate({
        search: qs.stringify(cleanNewQueries, { arrayFormat: 'comma', encode: false })
      });
    }
  };

  const removeQuery = removeQuery => {
    if (removeQuery && queries[removeQuery]) {
      delete queries[removeQuery];
      navigate({
        search: qs.stringify(queries, { arrayFormat: 'comma', encode: false })
      });
    }
  };

  const removeQueries = removeQuery => {
    const newQueries = { ...queries };

    removeQuery.forEach(key => {
      delete newQueries[key];
    });

    if (!isEqual(queries, newQueries)) {
      navigate({
        search: qs.stringify(newQueries, { arrayFormat: 'comma', encode: false })
      });
    }
  };

  const resetQueries = () => {
    navigate({
      search: ''
    });
  };

  return {
    queries,
    addQuery,
    removeQuery,
    removeQueries,
    addQueries,
    addNewQueries,
    resetQueries
  };
}
